import {
    useOrders,
    usePortfolioProfile,
    usePortfolioSelection,
} from "../../../../../shared/contexts/portfolio-context/components/PortfolioContextProvider";
import { useAppContext } from "../../../../../shared/contexts/app-context/components/AppContext";
import ReactEcharts from "echarts-for-react";
import { useEffect, useRef, useState } from "react";
import { Menu, MenuItem, Stack } from "@mui/material";
import { ConfigureProfileChartDialog } from "../../../../portfolio-info/components/ConfigureProfileChartDialog";
import { ComparePortfoliosDialog } from "../../../../portfolio-info/components/ComparePortfoliosDialog";
import { chartDataSourceService } from "./services/ChartDataSourceService";
import { chartDataSeriesService } from "./services/ChartDataSeriesService";
import { ProfileType } from "../../../../options-table/types/ProfileType";
import { chartTooltipService } from "../portfolio-greek-profile/services/ChartTooltipService";
import {
    MaximizedPortfolioBalanceChartDialog
} from "../../../../portfolio-info/components/MaximizedPortfolioBalanceChartDialog";
import { balanceProfileTooltipService } from "./services/BalanceProfileTooltipService";
import { BalanceProfilesSelector } from "../../BalanceProfilesSelector";
import { DeltaNeutralPriceSwitcher } from "../../DeltaNeutralPriceSwitcher";
import { PortfolioPerformanceSwitcher } from "../../PortfolioPerformanceSwitcher";
import { ChartSettingsButton } from "../../chart-settings-button/ChartSettingsButton";
import { CustomDateTimePicker } from "../../custom-date-time-picker/CustomDateTimePicker";
import { ComparedPortfolioSelector } from "../../ComparedPortfolioSelector";
import { DeltaValuesVisibilityToggle } from "../../DeltaValuesVisibilityToggle";
import { SavePortfolioZoomButton } from "../../SavePortfolioZoomButton";
import { TripleCustomBalanceToggle } from "../../TripleCustomBalanceToggle";
import { ResetPortfolioZoomButton } from "../../ResetPortfolioZoomButton";
import { positionsProcessor } from "../../../../positions-and-orders/utils/PositionsProcessor";
import { portfolioPerformanceService } from "../../../../../shared/services/PortfolioPerformanceService";
import { SessionBalanceAsZeroToggle } from "../../SessionBalanceAsZeroToggle";
import { numberFormatters } from "../../../../shared/utils/numberFormatters";

export function PortfolioBalanceProfile({ fullSize }: {fullSize: boolean}) {
    const selectedCurrentLineColour = 'green'; // '#444444';
    const selectedCustomLineColour = '#ac0000'; // '#444444';
    
    const {
        selectedPortfolio,
        selectedPortfolioDates,
    } = usePortfolioSelection();
    const {
        portfolioProfile,
        selectedComparedPortfolio,
        comparedPortfolioProfile,
    } = usePortfolioProfile();
    const {
        marketDataDto,
    } = useOrders();
    const {
        showBalancerPriceLines,
        balancerPriceLineDelta,
    } = useAppContext();
    
    const [isConfigureChartsDialogOpen, setIsConfigureChartsDialogOpen] = useState<boolean>(false);
    const [isComparePortfoliosDialogOpen, setComparePortfoliosDialogOpen] = useState<boolean>(false);
    const [isChartTooltipVisible, setIsChartTooltipVisible] = useState<boolean>(true);
    const [isMaximizedChartDialogOpen, setIsMaximizedChartDialogOpen] = useState<boolean>(false);
    
    const [contextMenu, setContextMenu] = useState<{
        mouseX: number;
        mouseY: number;
    } | null>(null);

    const chartRef = useRef<ReactEcharts | null>(null);
    
    useEffect(() => {
    }, [marketDataDto, portfolioProfile, comparedPortfolioProfile, selectedPortfolio]);
    
    if (selectedPortfolio === null || selectedPortfolio.portfolioSettings === null) {
        return null;
    }
    
    const settings = selectedPortfolio.portfolioSettings;
    const balanceDisplayShift = settings.useSessionBalanceAsZero
        ? settings.tradeSessionBalance
        : 0;
    
    const isProfileTypeSelected = (profileType: ProfileType, isCompared: boolean): boolean =>
        isCompared
            ? settings.selectedProfileTypes.indexOf(profileType) > -1 &&
                selectedPortfolio!.portfolioSettings!.useComparedPortfolio
            : settings.selectedProfileTypes.indexOf(profileType) > -1;
    
    const handleContextMenu = (event: React.MouseEvent) => {
        event.preventDefault();
        setContextMenu(
            contextMenu === null
                ? {
                    mouseX: event.clientX + 2,
                    mouseY: event.clientY - 6,
                }
                : null,
        );
    };
    
    const handleClose = (event: any) => {
        setContextMenu(null);
        event.stopPropagation();
    };

    const handleOpeningConfigureChartsDialog = (event: any) => {
        setContextMenu(null);
        event.stopPropagation();
        setIsConfigureChartsDialogOpen(true);
    };

    const handleShowMaximizedPortfolio = (event: any) => {
        setContextMenu(null);
        event.stopPropagation();
        setIsMaximizedChartDialogOpen(!isMaximizedChartDialogOpen);
    };

    const handleComparePortfoliosDialog = (event: any) => {
        setContextMenu(null);
        event.stopPropagation();
        setComparePortfoliosDialogOpen(true);
    };

    function handleChangeChartTooltipVisibility(event: any) {
        setContextMenu(null);
        event.stopPropagation();
        setIsChartTooltipVisible(!isChartTooltipVisible);
    }
    
    const dataSource: number[][] = chartDataSourceService.getDataSource(
        portfolioProfile, selectedPortfolio.portfolioSettings, comparedPortfolioProfile);
    const selectedPerformanceContext = portfolioPerformanceService.createContext(
        positionsProcessor.getSummarizedOrders(selectedPortfolio),
        selectedPortfolio,
    );
    const comparedPerformanceContext = selectedComparedPortfolio !== null
        ? portfolioPerformanceService.createContext(
            positionsProcessor.getSummarizedOrders(selectedComparedPortfolio),
            selectedComparedPortfolio,
        )
        : selectedPerformanceContext;
    const series: any[] = chartDataSeriesService.getChartBalanceSeries(
        marketDataDto!,
        portfolioProfile,
        comparedPortfolioProfile,
        selectedPortfolio.portfolioSettings,
        selectedPerformanceContext,
        showBalancerPriceLines,
        balancerPriceLineDelta,
        balanceDisplayShift,
    );
    
    const getXAxisName = (): string => 'Price\nCurrent\nCustom\nDelta\nVega\nTheta';
    
    const getAxisXFormattedLabel = (value: string, index: number) => {
        const processedPrice = parseFloat(value);
        const portfolioRecord = portfolioProfile.data.find(x => x.price === processedPrice);
        
        if (!portfolioRecord)
            return value;
        
        const currentBalance = portfolioRecord.currentDateBalance - balanceDisplayShift;
        const customBalance = portfolioRecord.customDateBalance1 - balanceDisplayShift;
        const delta = portfolioRecord.currentDelta;
        const vega = portfolioRecord.currentVega;
        const theta = portfolioRecord.currentTheta;
        
        return getLabelContent(processedPrice, currentBalance, customBalance, delta, vega, theta);
    }

    const isTopLabelsVisible =
        (selectedPortfolio.portfolioSettings.showSupportLine &&
            selectedPortfolio.portfolioSettings.supportLinePrice >= selectedPortfolio.portfolioSettings.minUnderlyingPrice) ||
        (selectedPortfolio.portfolioSettings.showResistanceLine &&
            selectedPortfolio.portfolioSettings.resistanceLinePrice <= selectedPortfolio.portfolioSettings.maxUnderlyingPrice);
    
    const getLabelContent = (
        processedPrice: number,
        currentBalance: number,
        customBalance: number,
        delta: number,
        vega: number,
        theta: number,
    ): string => {
        const newLine = '\n';
        const result = [];
        const currentBalanceToDisplay = settings.showPortfolioPerformance
            ? portfolioPerformanceService.formatMetric(
                currentBalance,
                selectedPerformanceContext,
                settings.usePortfolioAnnualisedRoi,
            )
            : numberFormatters.format0(currentBalance);
        const customBalanceToDisplay = settings.showPortfolioPerformance
            ? portfolioPerformanceService.formatMetric(
                customBalance,
                selectedPerformanceContext,
                settings.usePortfolioAnnualisedRoi,
            )
            : numberFormatters.format0(customBalance);

        result.push(numberFormatters.format0(processedPrice));
        result.push(`${newLine}`);
        result.push(currentBalanceToDisplay);
        result.push(`${newLine}`);
        result.push(customBalanceToDisplay);
        result.push(`${newLine}`);
        result.push(numberFormatters.format3(delta));
        result.push(`${newLine}`);
        result.push(numberFormatters.format0(vega));
        result.push(`${newLine}`);
        result.push(numberFormatters.format0(theta));
        result.push(`${newLine}`);
        
        return result.join('');
    }
    
    return (
        <Stack
            sx={{
                flex: '1',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                width: '100%',
                height: '100%',
            }}
            onContextMenu={handleContextMenu}
        >
            <ConfigureProfileChartDialog
                isOpen={isConfigureChartsDialogOpen}
                setIsOpen={setIsConfigureChartsDialogOpen}
            />
            <ComparePortfoliosDialog
                isOpen={isComparePortfoliosDialogOpen}
                setIsOpen={setComparePortfoliosDialogOpen}
            />
            <MaximizedPortfolioBalanceChartDialog
                isOpen={isMaximizedChartDialogOpen}
                setIsOpen={setIsMaximizedChartDialogOpen}
            />
            <Menu
                open={contextMenu !== null}
                onClose={handleClose}
                anchorReference="anchorPosition"
                anchorPosition={
                    contextMenu !== null
                        ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                        : undefined
                }
            >
                <MenuItem onClick={handleOpeningConfigureChartsDialog}>Configure chart...</MenuItem>
                <MenuItem onClick={handleComparePortfoliosDialog}>Compare portfolios...</MenuItem>
                <MenuItem onClick={handleChangeChartTooltipVisibility}>
                    {
                        isChartTooltipVisible
                            ? "Hide tooltip"
                            : "Show tooltip"
                    }
                </MenuItem>
                <MenuItem onClick={handleShowMaximizedPortfolio}>
                    {
                        isMaximizedChartDialogOpen
                            ? "Close maximized chart"
                            : "Show maximized chart"
                    }
                </MenuItem>
            </Menu>
            <Stack
                direction="row"
                spacing={0.75}
            >
                <BalanceProfilesSelector />
                <CustomDateTimePicker />
                <TripleCustomBalanceToggle />
                <ComparedPortfolioSelector />
                <DeltaNeutralPriceSwitcher />
                <PortfolioPerformanceSwitcher />
                <DeltaValuesVisibilityToggle />
                <SessionBalanceAsZeroToggle />
                <SavePortfolioZoomButton chartRef={chartRef} />
                <ResetPortfolioZoomButton />
                <ChartSettingsButton />
            </Stack>
            <div
                style={{
                    marginTop: '7px',
                    marginBottom: '7px',
                    borderBottom: 'solid 1px #bdbcbc',
                }}
            />
            <ReactEcharts
                ref={chartRef}
                theme="light"
                style={{
                    flex: '1',
                }}
                option={{
                    grid: {
                        left: 55,
                        right: 0,
                        top: isTopLabelsVisible ? '42px' : '10px', // 15px for each
                        bottom: 87,
                    },
                    legend: {
                        show: false,
                        selected: {
                            [balanceProfileTooltipService.currentBalanceName]: isProfileTypeSelected(ProfileType.Current, false),
                            [balanceProfileTooltipService.comparedCurrentBalanceName]: isProfileTypeSelected(ProfileType.Current, true),
                            [balanceProfileTooltipService.customBalance1Name]: isProfileTypeSelected(ProfileType.Custom, false),
                            [balanceProfileTooltipService.customBalance2Name]: isProfileTypeSelected(ProfileType.Custom, false) && settings.isTripleCustomBalanceEnabled,
                            [balanceProfileTooltipService.customBalance3Name]: isProfileTypeSelected(ProfileType.Custom, false) && settings.isTripleCustomBalanceEnabled,
                            [balanceProfileTooltipService.comparedCustomBalanceName]: isProfileTypeSelected(ProfileType.Custom, true),
                            [balanceProfileTooltipService.expirationBalanceName]: isProfileTypeSelected(ProfileType.Expiration, false),
                            [balanceProfileTooltipService.comparedExpirationBalanceName]: isProfileTypeSelected(ProfileType.Expiration, true),
                        },
                        top: 5,
                    },
                    tooltip: {
                        appendToBody: true,
                        trigger: 'axis',
                        showContent: isChartTooltipVisible,
                        formatter: (params: any) => balanceProfileTooltipService.getTooltipContent(
                            params,
                            marketDataDto!,
                            portfolioProfile,
                            comparedPortfolioProfile,
                            selectedPortfolioDates!,
                            settings.showPortfolioPerformance,
                            settings.usePortfolioAnnualisedRoi,
                            selectedPerformanceContext,
                            comparedPerformanceContext,
                            balanceDisplayShift,
                        ),
                        position: chartTooltipService.getTooltipPositionForBalanceProfileChart(
                            selectedPortfolio!.portfolioSettings!, fullSize),
                        backgroundColor: 'rgba(50,50,50,0.95)',
                        borderColor: 'rgba(200,200,200,1)',
                        textStyle: {
                            color: 'rgba(200,200,200,1)'
                        }
                    },
                    xAxis: {
                        type: 'category',
                        boundaryGap: false,
                        position: 'bottom',
                        nameLocation: 'start',
                        name: getXAxisName(),
                        nameTextStyle: {
                            fontWeight: '500',
                            align: 'right',
                            verticalAlign: 'top',
                            padding: [8, 0, 0, 0],
                            fontSize: 12,
                        },
                        nameGap: 3,
                        axisLabel: {
                            formatter: getAxisXFormattedLabel,
                            rich: {
                                currentBalanceStyle: {
                                    color: selectedCurrentLineColour,
                                    fontWeight: 'semibold',
                                },
                                customBalanceStyle: {
                                    color: selectedCustomLineColour,
                                    fontWeight: 'semibold',
                                },
                            },
                            align: 'right',
                            fontSize: 12,
                            showMinLabel: false,
                            interval: 'auto',
                            // hideOverlap: true,
                            // width: 5
                        },
                        axisLine: {
                            'onZero': false,
                        },
                    },
                    yAxis: [
                        {
                            gridIndex: 0,
                            position: 'left',
                            axisTick: {
                                show: true,
                                lineStyle: {
                                    color: 'rgb(200,200,200)'
                                },
                            },
                            splitLine: {
                                show: true,
                                lineStyle: {
                                    color: 'rgb(200,200,200)'
                                },
                            },
                            minorSplitLine: {
                                show: true,
                                splitNumber: 5,
                                lineStyle: {
                                    color: 'rgb(205,205,205)',
                                    type: 'dotted'
                                }
                            },
                            min: settings.useBalanceRangeRestriction ? settings.minBalance : undefined,
                            max: settings.useBalanceRangeRestriction ? settings.maxBalance : undefined,
                        },
                    ],
                    dataZoom: [
                        {
                            show: true,
                            type: 'inside',
                            zoomOnMouseWheel: true,
                            moveOnMouseMove: true,
                            moveOnMouseWheel: false,
                            filterMode: 'none',
                            xAxisIndex: [0],
                            startValue: settings.balanceStartValueX ?? 'min',
                            endValue: settings.balanceEndValueX ?? 'max',
                        },
                        {
                            show: true,
                            type: 'inside',
                            zoomOnMouseWheel: true,
                            moveOnMouseMove: true,
                            moveOnMouseWheel: false,
                            filterMode: 'none',
                            yAxisIndex: [0],
                            startValue: settings.balanceStartValueY ?? 'min',
                            endValue: settings.balanceEndValueY ?? 'max',
                        }
                    ],
                    dataset: {
                        source: dataSource,
                    },
                    series: series,
                }}
            />
        </Stack>
    );
}
